// Tenant permissions as the server resolves and enforces them
// (src/Cadence.Infrastructure/Services/TenantPermissions.cs). Mirrored here
// only so the admin Permissions screen can show each permission's role
// default as the admin toggles, and which ones a user can't hold; the
// server remains the judge and refuses anything these rules would refuse.

export type PermissionKey =
  | 'canManageStrategicDrivers'
  | 'canManageProjects'
  | 'canManageBilling'
  | 'canManageUsers'
  | 'canViewReports'
  | 'canManageWebhooks'
  | 'canManagePrompts'
  | 'canExportData';

/** Only an admin can hold these; an override can take them away, never grant them. */
export const ADMIN_ONLY_PERMISSIONS: ReadonlySet<PermissionKey> = new Set<PermissionKey>([
  'canManageBilling',
  'canManageUsers',
  'canManageWebhooks',
  'canManagePrompts',
]);

/** Admin for permission purposes: an admin role, or a tenant admin of any role. */
export function isAdminAccount(role: string | undefined, isTenantAdmin: boolean | undefined): boolean {
  return !!isTenantAdmin || role === 'Admin' || role === 'TenantAdmin' || role === 'PlatformAdmin';
}

/** What a user of this role gets when their override is left on the role default. */
export function roleDefault(key: PermissionKey, role: string | undefined, isTenantAdmin: boolean | undefined): boolean {
  const admin = isAdminAccount(role, isTenantAdmin);
  if (role === 'PlatformAdmin') return true;
  if (ADMIN_ONLY_PERMISSIONS.has(key)) return admin;
  if (key === 'canManageStrategicDrivers') return admin || role === 'Executive';
  return true; // projects, reports, export: everyone
}

/**
 * Why a permission can't be set to Yes for this user, or null when it can.
 * Admin-only permissions stay admin-only on the server, so granting one to
 * anyone else would be a switch that does nothing.
 */
export function grantUnavailableReason(
  key: PermissionKey, role: string | undefined, isTenantAdmin: boolean | undefined,
): string | null {
  return ADMIN_ONLY_PERMISSIONS.has(key) && !isAdminAccount(role, isTenantAdmin)
    ? 'Needs a tenant admin'
    : null;
}
